// Which animation an action looks like.
//
// The sim names actions (MOVE, FORAGE, SLEEP...); the clips in bird.glb are
// named for what the animator drew (BirdWalk, Eat1, Sleep...). The two do
// not have to match, and forcing them to would have meant re-exporting the
// model to suit the code, which is the wrong way round. They meet here.
//
// Anything unmapped falls through to IDLE, so a new action added to the sim
// stands and breathes rather than crashing or freezing in the bind pose.

import { BAG_MOVES } from '../things/bag.js'

export const IDLE_CLIP = 'Idle1'

// Every clip bird.glb carries. A move that names any other plays nothing,
// so the things kit's test holds every move to this list, and a new clip
// from Sam goes here the day it lands in the model.
export const BIRD_CLIPS = ['BirdWalk', 'Eat1', 'Idle1', 'Inspect', 'Rummage', 'Sit', 'SitToStand', 'Sleep', 'StandToSit', 'Watch']

export const ACTION_CLIP = {
  MOVE: 'BirdWalk',
  IDLE: IDLE_CLIP,

  FORAGE: 'Eat1',
  SCAVENGE: 'Rummage',
  // looking along the station floor round the ticket machine for old tickets (sim-server world/Tickets.js)
  TICKETS: 'Rummage',
  INSPECT: 'Inspect',

  // Sitting down for a while. SLEEP and REST share a body position and
  // differ by the clip: Sleep is settled and still, Sit is awake.
  REST: 'Sit',
  SLEEP: 'Sleep',

  // Absorbed in something at a distance, which is the same body language
  // whether it is a screen, a film, a drum of washing or a needle behind
  // glass. One clip, four activities, and it is the second most-seen thing
  // he does after walking.
  BROWSE: 'Watch',
  WATCH: 'Watch',
  LAUNDRY: 'Watch',
  TATTOO: 'Watch',

  // Looking closely at a particular thing rather than at a screen.
  MUSEUM: 'Inspect',
  JUDGE: 'Inspect',
  SURVEY: 'Watch',

  // His bag's moves, if the town gives them a verb: lib/things/bag.js says
  // the clip each plays, so the town's things kit and the bird read one row.
  PUT_AWAY: BAG_MOVES.put_away.clip,
  TAKE_OUT: BAG_MOVES.take_out.clip,
  PICK_UP: BAG_MOVES.pick_up.clip,

  // No clip of their own yet, so they read as standing rather than as the
  // wrong thing. Listed explicitly so the gap is visible: these are the
  // next ones worth drawing.
  PADDLE: IDLE_CLIP,
  DRINK: IDLE_CLIP,
  PREEN: IDLE_CLIP,
  PHONE: IDLE_CLIP,
  RAVE: IDLE_CLIP,
  GAMBLE: IDLE_CLIP,
  SOCIALISE: IDLE_CLIP,
  POST_TO_TWITTER: IDLE_CLIP,
}

// Clips performed sitting down. Getting into or out of one of these plays a
// transition first, so he does not pop from standing to seated.
export const SEATED_CLIPS = new Set(['Sit', 'Sleep'])

export const STAND_TO_SIT = 'StandToSit'
export const SIT_TO_STAND = 'SitToStand'

// The places he asked for at the shrine share one verb, GRANTED, and what
// he looks like there is the place's: sat on the bench, looking out at the
// weather from under the shelter's roof. Keyed by the sim's entity id.
export const GRANTED_CLIP = {
  activity_granted_bench: 'Sit',
  activity_granted_shelter: 'Watch',
  activity_granted_keyroom: 'Sit',
  activity_granted_market: 'Watch',
}

/** The clip for an action, always something playable. */
export function clipForAction(action, targetId = null) {
  if (!action) return IDLE_CLIP
  if (action === 'GRANTED') return GRANTED_CLIP[targetId] || IDLE_CLIP
  return ACTION_CLIP[action] || IDLE_CLIP
}
